import type {
  FoodOutDetailsListModel,
  KitchenTaskStartResultModel,
  KitchenScheduleSettingsModel,
} from '@/model/kitchen';
import { changeKitchenStatus } from '@/service/kitchen';

import { isKitchenTaskScheduled } from '@/service/kitchenSchedule';

const batchWindow = 3 * 60 * 1000;

const taskTime = (task: FoodOutDetailsListModel): number =>
  task.createTime ? new Date(task.createTime.replace(' ', 'T')).getTime() : NaN;

export const isSameKitchenDish = (
  first: FoodOutDetailsListModel,
  second: FoodOutDetailsListModel,
): boolean => {
  const sameFood =
    first.shopFoodId && second.shopFoodId
      ? Number(first.shopFoodId) === Number(second.shopFoodId)
      : Boolean(first.foodId && second.foodId && Number(first.foodId) === Number(second.foodId));
  return Boolean(
    sameFood &&
      Number(first.shopId) === Number(second.shopId) &&
      Number(first.kitchenId) === Number(second.kitchenId) &&
      Number(first.shopFoodSpecId || first.foodSpecId || 0) ===
        Number(second.shopFoodSpecId || second.foodSpecId || 0) &&
      (first.foodSpecName || '').trim() === (second.foodSpecName || '').trim() &&
      (first.foodCookDesc || '').trim() === (second.foodCookDesc || '').trim() &&
      (first.orderCookDesc || '').trim() === (second.orderCookDesc || '').trim(),
  );
};

export const canCookKitchenTasksTogether = (
  first: FoodOutDetailsListModel,
  second: FoodOutDetailsListModel,
): boolean =>
  isSameKitchenDish(first, second) && Math.abs(taskTime(first) - taskTime(second)) <= batchWindow;

export const selectKitchenStartBatch = (
  task: FoodOutDetailsListModel,
  tasks: readonly FoodOutDetailsListModel[],
  scheduleSettings: KitchenScheduleSettingsModel,
): FoodOutDetailsListModel[] => {
  if (isKitchenTaskScheduled(task, scheduleSettings)) return [];
  const related = tasks.filter(
    (item) =>
      !isKitchenTaskScheduled(item, scheduleSettings) &&
      item.orderOutId !== task.orderOutId &&
      Number(item.stoveId || 0) === Number(task.stoveId || 0) &&
      [0, 5].includes(item.kitchenStatus) &&
      item.operateStatus !== 1 &&
      ![8, 9, 999].includes(item.status) &&
      canCookKitchenTasksTogether(task, item),
  );
  // 以最早发送到厨房的时间限制整批跨度，避免相邻三分钟不断串联成更长的一批。
  const earliest = Math.min(taskTime(task), ...related.map(taskTime));
  return [
    task,
    ...related.filter(
      (item) => item.kitchenStatus === 0 && taskTime(item) - earliest <= batchWindow,
    ),
  ];
};

export const startKitchenTasksTogether = async (
  tasks: readonly FoodOutDetailsListModel[],
  shouldContinue: () => boolean,
): Promise<KitchenTaskStartResultModel> => {
  const result: KitchenTaskStartResultModel = { startedTaskIds: [], failedCount: 0, message: '' };
  for (const task of tasks) {
    if (!shouldContinue()) break;
    if (task.kitchenStatus === 5) continue;
    try {
      const response = await changeKitchenStatus({ orderOutId: task.orderOutId, kitchenStatus: 5 });
      if (response.code !== 200) throw new Error(response.msg || '开始制作失败');
      result.startedTaskIds.push(task.orderOutId);
    } catch (error) {
      console.error(`开始制作菜品 ${task.orderOutId} 失败`, error);
      result.failedCount++;
      if (!result.message) result.message = error instanceof Error ? error.message : '开始制作失败';
    }
  }
  return result;
};
